import { LoginScreen, type LoginResult, type LoginScreenEndpoints } from '@cca/ui';
import { storeSessionToken } from '../api/client';
import { GENERAL_PORTAL_NAME } from '../portal-name';

// The CCA-decides door adopts the estate's ONE sign-in screen (ADR-018 /
// AUTH-8). There is deliberately no login component in this package beyond this
// file, and this file draws no sign-in primitive of its own — it mounts
// <LoginScreen> and stores what comes back.
//
// ── PASSKEY-FIRST, WHICH IS THE DEFAULT AND THEREFORE NOT SPELLED ──────────
// This file used to pass `mode="otp-only"` because the worker bound no credential
// store. It binds one now, so the prop is gone and `<LoginScreen>`'s default
// (`passkey-first`) applies: the one-tap button, the conditional-UI autofill and
// the OTP recovery path underneath it.
//
// NOTHING ELSE WAS ADDED HERE, DELIBERATELY. The whole ceremony — arming
// conditional UI on mount, the `browserSupportsWebAuthnAutofill()` gate, the
// AbortError/StrictMode cleanup, gating `enabled` to the login step — lives
// INSIDE <LoginScreen>, which calls `usePasskeyAutofill` itself
// (@cca/ui LoginScreen.tsx:332). A door that reached for `usePasskeyAutofill` or
// `startAuthentication` directly would be the residual login clone AUTH-8's
// detector is built to find. The six endpoint paths below were already spelled
// for this day; all six exist now.
//
// WHAT THE BUTTON CAN AND CANNOT DO. A passkey proves who you are; it does not
// seat you. The worker re-reads the grant register on verify and answers 403
// `no_grant` to an address with no live Admin or Scrutineer role, which the
// screen renders as `authzDeniedCopy` below — the same refusal, in the same
// words, whichever credential was presented.
//
// ── THE COLOUR IS AN INVARIANT, NOT A CHOICE ───────────────────────────────
// <LoginScreen>'s `accentColor` styles the whole screen, and it MUST equal the
// accent the worker hands `renderOtpCodeEmail` — the screen and the login-code
// email are two faces of one door, and if they drift the person who just saw a
// teal screen gets a gold email. The worker's copy is `DECIDES_ACCENT_COLOR` in
// packages/cca-decides-worker/src/auth.ts. Change one, change the other.
// #1d5b63 is also `--brand-decides` in @cca/ui's token file; it is written as a
// literal here because a CSS custom property is not a value a prop can read.

/** Teal, measured 7.70:1 on white and 6.95:1 on the shared cream. Teal because
 *  the RESULTS own green / amber / red — a signature colour in one of those
 *  families would put the page chrome in the same hue as a verdict. */
const DECIDES_ACCENT = '#1d5b63';

/** Where the worker lives. The same base the API client uses; the two are
 *  separate because <LoginScreen> takes full paths and hits them with its own
 *  fetch (a pre-auth 404 is an expected branch there, not a telemetry event). */
const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

/**
 * The six paths <LoginScreen> can talk to. ALL SIX EXIST on the worker now — the
 * passkey four are `createPasskeySignInRoutes` mounted at
 * `/api/auth/passkeys` (packages/cca-decides-worker/src/routes/auth.ts), the OTP
 * two are `createOtpSignInRoutes`. This block predicted that day and was written
 * to need no edit when it came, which is why the strings are unchanged: the door
 * needed exactly the one change the old comment said it would, dropping the mode.
 */
const DECIDES_ENDPOINTS: LoginScreenEndpoints = {
  discoverOptions: `${API_BASE}/api/auth/passkeys/discover-options`,
  discoverVerify: `${API_BASE}/api/auth/passkeys/discover-verify`,
  passkeyOptions: `${API_BASE}/api/auth/passkeys/options`,
  passkeyVerify: `${API_BASE}/api/auth/passkeys/verify`,
  otpRequest: `${API_BASE}/api/auth/otp/request`,
  otpVerify: `${API_BASE}/api/auth/otp/verify`,
};

interface Props {
  /** Called once the token is stored, so the shell can ask the server who this
   *  is. The identity is NOT passed through here: the server decides it, on a
   *  fresh request, every time. */
  onSignedIn: () => void;
}

export function DecidesSignIn({ onSignedIn }: Props) {
  const applySession = (result: LoginResult): void => {
    // `token` is the whole payload this door returns — no role, deliberately.
    // A role handed over at sign-in and kept by the browser would outlive its own
    // revocation for a whole rolling session; the shell asks /api/auth/me instead.
    if (typeof result.token !== 'string' || result.token === '') return;
    storeSessionToken(result.token);
    onSignedIn();
  };

  return (
    <LoginScreen
      // THE GENERAL NAME, ALWAYS. Nobody is signed in at this screen, so no role
      // exists to name — and the general one is also the Scrutineer's, so the
      // word does not change under them the moment they get in. The reasoning in
      // full, and the worker's matching copy for the login-code email, are in
      // ../portal-name.ts.
      portalName={GENERAL_PORTAL_NAME}
      accentColor={DECIDES_ACCENT}
      endpoints={DECIDES_ENDPOINTS}
      onSignedIn={applySession}
      // The 403 this door actually produces is `no_grant`, and the generic
      // estate copy ("you are not authorised to access this portal") would be
      // read by a member of Synod as a fault with their account. It is not: the
      // platform is run by the Admin and the Scrutineers, and everybody else is
      // correctly outside it. Say which, and say who to ask.
      authzDeniedCopy={`The ${GENERAL_PORTAL_NAME} is used by the Synod Admin and the Scrutineers to run a sitting. Your address is not recorded as holding either role, so there is nothing here for you to sign in to — a member of Synod hears the result when the President declares it. If you believe you should have access, contact registrar@confessinganglicans.nz.`}
      copy={{
        signInSubtitle:
          'For the Synod Admin and the Scrutineers. Enter your email address to receive a login code.',
        // NOT "try again in an hour" (the estate default), because on this door
        // that is false for the case that matters. A synod signs in behind one
        // venue IP, and both of the worker's per-IP budgets - the passkey probe
        // the screen calls on every submit, and the OTP door - run a 60-second
        // window (packages/cca-decides-worker/src/routes/auth.ts, cca-883vu /
        // cca-4ki98). Told "an hour", a member in the hall would give up on a
        // door that reopens in a minute.
        //
        // "A FEW MINUTES" AND NOT "A MINUTE", because this one string answers
        // every 429 the door can send and the screen cannot tell them apart (all
        // say `rate_limited`): the two per-IP windows (60 s), the wrong-code
        // lockout (10 failures in a sliding 15 minutes) and the per-address cap
        // (5 codes an hour). It is honest for the two 60-second windows. For the
        // lockout and the per-address cap it undersells the wait - two named
        // residuals, recorded in docs/standards/auth.md - which is why it ENDS
        // with UX-6's escape hatch, word for word (docs/standards/ux.md UX-6): a
        // member still refused after a few minutes is told who can help.
        rateLimited:
          'Too many sign-in attempts. Please wait a few minutes and try again. Having any problems? Email registrar@confessinganglicans.nz.',
      }}
      footer={`CCAANZ ${GENERAL_PORTAL_NAME} · ${new Date().getFullYear()}`}
    />
  );
}
